import clsx from 'clsx'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface TooltipProps {
  content?: string
  children: React.ReactNode
  className?: string
  position?: 'top' | 'bottom'
  tooltipClassName?: string
  hide?: boolean
  config?: {
    maxWidth?: string
    delay?: number
    allowHTML?: boolean
  }
}

interface Coords {
  top: number
  left: number
}

export const Tooltip = ({
  content,
  children,
  className,
  position = 'top',
  tooltipClassName,
  hide,
  config,
}: TooltipProps) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState<Coords>({ top: 0, left: 0 })

  const hasTooltip = Boolean(content)

  // Tailwind utilities are scoped under `#nma-gallery-root` (see the config's
  // `important` selector), so the portal must render *inside* that root — in
  // `document.body` none of the utility classes below would apply. The root
  // also sits above the dropdown's overflow container, so the tooltip still
  // escapes clipping.
  const portalTarget = (typeof document !== 'undefined' && document.getElementById('nma-gallery-root')) || document.body

  // Position relative to the viewport (position: fixed) and render through a
  // portal so the tooltip escapes any ancestor overflow/clipping — e.g. the
  // dropdown's `overflow-y-auto` list that would otherwise cut it off.
  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return

    const triggerRect = trigger.getBoundingClientRect()
    const tooltipRect = tooltipRef.current?.getBoundingClientRect()
    const tooltipHeight = tooltipRect?.height ?? 0
    const tooltipWidth = tooltipRect?.width ?? 0

    const GAP = 8
    const PADDING = 8

    let top = position === 'top' ? triggerRect.top - tooltipHeight - GAP : triggerRect.bottom + GAP
    let left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2

    // Keep within the viewport horizontally.
    if (left < PADDING) left = PADDING
    if (left + tooltipWidth > window.innerWidth - PADDING) {
      left = window.innerWidth - tooltipWidth - PADDING
    }

    // Flip vertically if it would overflow the viewport.
    if (top < PADDING && position === 'top') top = triggerRect.bottom + GAP
    if (top + tooltipHeight > window.innerHeight - PADDING && position === 'bottom') {
      top = triggerRect.top - tooltipHeight - GAP
    }

    setCoords({ top, left })
  }, [position])

  const showTooltip = () => {
    if (hide || !hasTooltip) return
    setVisible(true)
  }

  const hideTooltip = () => setVisible(false)

  // Recompute once visible (after the node has dimensions) and keep it pinned
  // to the trigger while scrolling/resizing.
  useLayoutEffect(() => {
    if (!visible) return

    updatePosition()

    const handleReposition = () => updatePosition()
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [visible, updatePosition])

  return (
    <div
      ref={triggerRef}
      className={clsx('relative inline-block', className)}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}

      {hasTooltip &&
        visible &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              maxWidth: config?.maxWidth,
              transitionDelay: config?.delay ? `${config.delay}ms` : undefined,
              zIndex: 9999,
            }}
            className={clsx(
              'py-[6px] px-2 bg-[#505050] rounded-lg shadow-lg text-wrap whitespace-pre-wrap w-max',
              tooltipClassName,
            )}
          >
            <div className="text-[#fff] text-sm leading-[22px] text-left max-w-[350px]">{content}</div>

            {/* Arrow */}
            <div
              className={clsx(
                'absolute left-1/2 -translate-x-1/2 size-2 rounded-[1px] bg-[#505050] shadow-lg',
                position === 'top' ? 'top-full -mt-1 rotate-45' : 'bottom-full -mb-1 -rotate-45',
              )}
            />
          </div>,
          portalTarget,
        )}
    </div>
  )
}
